import { Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type { EditFixtureForm } from "~pages/Fixtures/types";
import Page from "../../../Common/Page";
import {
  FixtureAdvancedSection,
  FixtureBasicSection,
  FixtureCompetitorsSection,
  FixtureEntrantsSkeleton,
  FixtureParticipantsSection,
} from "./components";
import { formStyles, StyledFormGroupsContainer } from "./FixtureEditPage.styles";
import { useEditFixtureFormSubmit, useEditFixturePageTitle } from "./hooks";
import { StyledEntrantSection } from "./styles";

const FixtureEditPage = () => {
  const { formatMessage } = useIntl();
  const { data: fixtureData, status: fixtureQueryStatus, isLoading, isFetching } = useGetFixture();
  const [isUpdating, setIsUpdating] = useState(false);

  const [form] = Form.useForm<EditFixtureForm>();

  // form sync with BE
  useEffect(() => {
    if (fixtureQueryStatus === "success" && fixtureData) {
      form.setFieldsValue(fixtureData.formData);
    }
  }, [fixtureData, fixtureQueryStatus, form]);

  // form submission, notifications are handled by `useFixtureService`
  const onFinish = useEditFixtureFormSubmit();

  const pageTitles = useEditFixturePageTitle();
  const { hasPermission } = useUserPermissions();

  const isCompetitors = fixtureData?.competitorType === CompetitorTypes.Entity;
  const isParticipants = fixtureData?.competitorType === CompetitorTypes.Person;

  const readFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.READ);
  const readParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.READ);
  const readCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.READ);
  const nothingAllowed = !readFixtureAllowed && !readParticipantsAllowed && !readCompetitorsAllowed;

  const editFixtureAllowed = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const editParticipantsAllowed = hasPermission(PermissionNames.FIXTURE_PERSON, PermissionOperations.UPDATE);
  const editCompetitorsAllowed = hasPermission(PermissionNames.FIXTURE_ENTITY, PermissionOperations.UPDATE);
  const isSubmitAllowed = editFixtureAllowed && (isParticipants ? editParticipantsAllowed : editCompetitorsAllowed);

  const navigate = useNavigate();
  const { customerId, sport, organizationId } = useParams();
  useEffect(() => {
    if (nothingAllowed) navigate(`/${customerId}/admin/${sport}/${organizationId}/403`);
  }, [nothingAllowed, customerId, sport, organizationId, navigate]);

  return (
    <Page {...pageTitles}>
      <Form<EditFixtureForm>
        form={form}
        onFinish={async (values) => {
          setIsUpdating(true);
          try {
            await onFinish(values);
          } finally {
            setIsUpdating(false);
          }
        }}
        style={formStyles}
      >
        <StyledFormGroupsContainer>
          <FixtureBasicSection isUpdating={isUpdating} />
          {readFixtureAllowed && <FixtureAdvancedSection isUpdating={isUpdating} />}

          {isLoading && <FixtureEntrantsSkeleton />}
          <StyledEntrantSection>
            {isCompetitors && readCompetitorsAllowed && (
              <>
                <FixtureCompetitorsSection isUpdating={isUpdating} isHome />
                <FixtureCompetitorsSection isUpdating={isUpdating} />
              </>
            )}
            {isParticipants && readParticipantsAllowed && (
              <>
                <FixtureParticipantsSection isUpdating={isUpdating} isHome />
                <FixtureParticipantsSection isUpdating={isUpdating} />
              </>
            )}
          </StyledEntrantSection>
        </StyledFormGroupsContainer>
        {isSubmitAllowed && (
          <Button htmlType="submit" type="primary" disabled={isUpdating} loading={(isFetching || isUpdating) && !isLoading}>
            {formatMessage({ id: "fixture.update.button" })}
          </Button>
        )}
      </Form>
    </Page>
  );
};

export default FixtureEditPage;
